import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import CopyIcon from "static/images/CopyPurple.svg";
import { BankTransferOrder } from "store/apis/bankTransfer";

const formatAddress = (address: {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}) =>
  [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

const CopyableValue = ({
  value,
  onCopy,
}: {
  value: string;
  onCopy: () => void;
}) => (
  <div className="t-flex t-gap-1.5 t-items-center">
    <span className="t-text-text-100 t-text-body-sm">{value}</span>
    <img src={CopyIcon} alt="copyIcon" role="button" onClick={onCopy} />
  </div>
);

export const BankAccountDetails = ({
  bankTransferOrder,
}: {
  bankTransferOrder: BankTransferOrder;
}) => {
  const { copyToClipboard } = useCopyToClipboard();
  const { type, supported_networks } = bankTransferOrder.bank_transfer_details;
  const bankDetails = bankTransferOrder.bank_transfer_details[type];

  if (!bankDetails) return null;

  const accountHolderAddress = formatAddress(
    bankDetails.account_holder_address
  );
  const bankAddress = formatAddress(bankDetails.bank_address);

  return (
    <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg">
      <div className="t-border t-border-solid t-border-neutral-10 t-border-t-0 t-border-r-0 t-border-l-0 t-px-4 t-py-3">
        <div className="t-text-neutral-60 t-text-sm">Account Details</div>
        <div className="t-text-body-sm t-text-text-30">
          Use the below account details to make the transfer
        </div>
      </div>
      <div className="t-px-4 t-py-5 t-space-y-5">
        <InfoItem label="Bank">
          <CopyableValue
            value={bankDetails.bank_name}
            onCopy={() => copyToClipboard(bankDetails.bank_name)}
          />
        </InfoItem>
        <InfoItem label="A/C Number">
          <CopyableValue
            value={bankDetails.account_number}
            onCopy={() => copyToClipboard(bankDetails.account_number)}
          />
        </InfoItem>
        {type === "swift" && bankDetails.swift_code && (
          <InfoItem label="Swift Code">
            <CopyableValue
              value={bankDetails.swift_code}
              onCopy={() => copyToClipboard(bankDetails.swift_code!)}
            />
          </InfoItem>
        )}
        {type === "aba" && bankDetails.routing_number && (
          <InfoItem label="Routing Number">
            <CopyableValue
              value={bankDetails.routing_number}
              onCopy={() => copyToClipboard(bankDetails.routing_number!)}
            />
          </InfoItem>
        )}
        <InfoItem label="Account Type">
          <span className="t-text-text-100 t-text-body-sm t-capitalize">
            {bankDetails.account_type}
          </span>
        </InfoItem>
        <InfoItem label="Payment Method">
          <span className="t-text-text-100 t-text-body-sm">
            {supported_networks.join("/")}
          </span>
        </InfoItem>
        <InfoItem label="Account Holder Name">
          <CopyableValue
            value={bankDetails.account_holder_name}
            onCopy={() => copyToClipboard(bankDetails.account_holder_name)}
          />
        </InfoItem>
        <InfoItem label="Account Holder Address">
          <CopyableValue
            value={accountHolderAddress}
            onCopy={() => copyToClipboard(accountHolderAddress)}
          />
        </InfoItem>
        <InfoItem label="Bank Address">
          <CopyableValue
            value={bankAddress}
            onCopy={() => copyToClipboard(bankAddress)}
          />
        </InfoItem>
      </div>
    </div>
  );
};
